// kanjo/Cashflow.jsx — 13-week forecast (US-3.4, US-3.5) + AP aging (US-3.2).
// Style aligned with the mockup design system: figure-xl cash position +
// per-account breakdown rows + axis-aware forecast bar chart + AP buckets.

function fmtMoneyC(cents, currency, locale) {
  const c = window.kanjo?.demo?.scale ? window.kanjo.demo.scale(cents) : cents;
  return new Intl.NumberFormat(locale || 'en-US', { style: 'currency', currency: currency || 'USD' })
    .format((c || 0) / 100);
}
// Delegates to Today.jsx's locale-aware moneyParts so symbol order is right
// in every locale (fr-FR puts the symbol after the number).
function moneyPartsC(cents, currency, locale) {
  return moneyParts(cents, currency, locale);
}

// ── FundingGauge: bank balance vs weeks-of-expenses runway ───────────────────
// Zones (owner rule 2026-07-08): red below 1 week of the all-in expense rate
// (rent, payroll, suppliers, insurance — everything, averaged over the last 12
// weeks), yellow below the 3-week minimum funding line, green at/above. The
// track shows the three zones; the solid fill is the actual balance.
function FundingGauge({ funding, cashCents, currency, locale }) {
  if (!funding || !(funding.weeklyExpenseCents > 0)) return null;
  const fmt = (c) => fmtMoneyC(c, currency, locale);
  const tone = funding.level === 'danger' ? 'var(--danger)'
    : funding.level === 'warning' ? 'var(--warning)' : 'var(--positive)';
  // Scale to 4 weeks of expenses (a bit past the green line) or the balance,
  // whichever is larger, so the fill never clips.
  const scale = Math.max(funding.weeklyExpenseCents * 4, cashCents, 1);
  const pctOf = (c) => Math.max(0, Math.min(100, (c / scale) * 100));
  const p1 = pctOf(funding.dangerCents);   // 1 week — danger line
  const p3 = pctOf(funding.minCents);      // 3 weeks — minimum funding line
  const fill = pctOf(cashCents);
  const weeks = funding.weeksCover;
  return (
    <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--rule)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', fontSize: 13 }}>
        <div style={{ color: 'var(--ink-3)' }}>Funding runway</div>
        <div className="mono" style={{ color: tone, fontWeight: 700 }}>
          {weeks != null ? `≈ ${weeks >= 10 ? Math.round(weeks) : weeks.toFixed(1)} wk of expenses` : '—'}
        </div>
      </div>
      <div style={{ position: 'relative', height: 10, borderRadius: 5, overflow: 'hidden', marginTop: 8 }}>
        {/* zone track: red → 1 wk, yellow → 3 wks, green beyond */}
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${p1}%`, background: 'var(--danger)', opacity: 0.22 }}/>
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${p1}%`, width: `${Math.max(0, p3 - p1)}%`, background: 'var(--warning)', opacity: 0.22 }}/>
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${p3}%`, right: 0, background: 'var(--positive)', opacity: 0.22 }}/>
        {/* the balance */}
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${fill}%`, background: tone, borderRadius: 5 }}/>
        {/* threshold ticks */}
        <div style={{ position: 'absolute', top: -1, bottom: -1, left: `calc(${p1}% - 1px)`, width: 2, background: 'var(--surface)', opacity: 0.9 }}/>
        <div style={{ position: 'absolute', top: -1, bottom: -1, left: `calc(${p3}% - 1px)`, width: 2, background: 'var(--surface)', opacity: 0.9 }}/>
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 8, lineHeight: 1.5 }}>
        Expenses run ≈ <span className="mono" style={{ color: 'var(--ink-2)' }}>{fmt(funding.weeklyExpenseCents)}</span>/week
        (everything — rent, payroll, suppliers, insurance — last {Math.round(funding.weeksObserved)} wk).
        Minimum funding <span className="mono" style={{ color: 'var(--warning)' }}>{fmt(funding.minCents)}</span> (3 wk) ·
        danger below <span className="mono" style={{ color: 'var(--danger)' }}>{fmt(funding.dangerCents)}</span> (1 wk).
      </div>
    </div>
  );
}

// The two states of a choice chip, kept as SEPARATE objects because they use
// different colour pairs: selected is the brand tint with brand text (a --*-soft
// tint is translucent, so only its own bright colour reads on it); unselected is
// plain --ink-2 on the card. Splitting them keeps each pair intact.
const PICK_BASE = { flex: 1, padding: '7px 8px', borderRadius: 8, cursor: 'pointer', fontSize: 12.5, fontWeight: 600 };
const PICK_ON = { ...PICK_BASE, border: '1px solid var(--brand)', background: 'var(--brand-soft)', color: 'var(--brand)' };
const PICK_OFF = { ...PICK_BASE, border: '1px solid var(--rule)', background: 'transparent', color: 'var(--ink-2)' };

// ── ClassifyDrawsSheet: which draws were W2, which flushed the pot ─────────
// The operator's marking is the truth. The one-off suggestion is shown as a
// hint, never applied on its own — that heuristic is exactly what got this
// wrong before, so it may propose but not decide.
function ClassifyDrawsSheet({ fmt, onChanged }) {
  useT();
  const [st, setSt] = React.useState({ loading: true, rows: [] });
  // Candidates are OFF by default. Offering every large payment buries the real
  // draws under the rent and the tax bills — it is a search for a specific
  // missing transfer, not the normal view.
  const [candidates, setCandidates] = React.useState(false);
  const load = React.useCallback(async (withCandidates) => {
    try {
      const od = window.kanjo?.cashflow?.ownerDraw;
      // Year to date: reconciling distributions needs the whole year, not the
      // most recent handful.
      const txs = await od.recentDraws({ candidates: !!withCandidates });
      const { looksLikeRetainedDraw } = window.kanjo.cashflow.retainedBalance;
      setSt({ loading: false, rows: txs.map((t) => ({ ...t, suggested: looksLikeRetainedDraw(t, txs) })) });
    } catch (e) { setSt({ loading: false, rows: [], error: String(e?.message || e) }); }
  }, []);
  React.useEffect(() => { load(candidates); }, [load, candidates]);

  async function pick(row, kind) {
    setSt((p) => ({ ...p, rows: p.rows.map((r) => r.plaidTxId === row.plaidTxId ? { ...r, kind } : r) }));
    try {
      await window.kanjo.cashflow.ownerDraw.setDrawKind(row.plaidTxId, kind);
      onChanged?.();
    } catch (e) { console.warn('[classify] failed', e); load(candidates); }
  }

  if (st.loading) return <SkeletonCard height={160}/>;
  if (!st.rows.length) return (
    <div style={{ fontSize: 13, color: 'var(--ink-3)', padding: '8px 0' }}>
      {t('cf.noDraws', 'No owner draws found yet. Add a draw keyword in settings so Kanjo can spot them.')}
    </div>
  );
  return (
    <div>
      {/* YTD totals by kind, so the year reconciles at a glance. */}
      {(() => {
        const sum = (k) => st.rows.filter((r) => (r.kind || 'w2') === k)
          .reduce((a, r) => a + Math.abs(r.amountCents || 0), 0);
        const rows = [
          [t('cf.w2Draw', 'W2 / payroll'), sum('w2')],
          [t('cf.retainedDraw', 'Retained draw'), sum('retained')],
          [t('cf.retainedPriorDraw', 'Retained — last year'), sum('retainedPrior')],
        ];
        return (
          <div style={{ marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid var(--rule)' }}>
            <div className="label" style={{ marginBottom: 6 }}>
              {t('cf.ytdDraws', 'Year to date').replace('{n}', String(st.rows.length))}
            </div>
            {rows.map(([lbl, cents]) => (
              <div key={lbl} style={{ display: 'flex', justifyContent: 'space-between',
                gap: 10, fontSize: 12.5, color: 'var(--ink-2)', padding: '2px 0' }}>
                <span>{lbl}</span>
                <span className="mono" style={{ fontWeight: 600, color: 'var(--ink)' }}>{fmt(cents)}</span>
              </div>
            ))}
          </div>
        );
      })()}
      <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginBottom: 12, lineHeight: 1.45 }}>
        {t('cf.classifyHelp', 'A W2 draw runs through payroll, against earnings from the same month. A retained draw is cash taken out of profit already earned — it comes off the retained balance instead.')}
      </div>
      {st.rows.length === 0 && candidates && (
        <div style={{ fontSize: 12.5, color: 'var(--ink-3)', padding: '4px 0 10px' }}>
          {t('cf.noCandidates', 'No other large payments to consider.')}
        </div>
      )}
      {st.rows.map((r) => (
        <div key={r.plaidTxId} style={{ padding: '10px 0', borderTop: '1px solid var(--rule)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline' }}>
            <span style={{ fontSize: 13, color: 'var(--ink-2)', minWidth: 0 }}>{r.date}</span>
            <span className="mono" style={{ fontSize: 14, fontWeight: 600 }}>{fmt(Math.abs(r.amountCents || 0))}</span>
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--ink-3)', margin: '2px 0 6px' }}>{r.name || r.merchantName || ''}</div>
          {/* Three kinds, because they are three different events: payroll
              against this month's earnings, a distribution of THIS year's kept
              profit, and a distribution against LAST year's closing balance —
              the last was already income in the year it was earned. */}
          <div role="radiogroup" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[['w2', t('cf.w2Draw', 'W2 / payroll')],
              ['retained', t('cf.retainedDraw', 'Retained draw')],
              ['retainedPrior', t('cf.retainedPriorDraw', 'Retained — last year')]].map(([val, lbl]) => {
              const on = (r.kind || 'w2') === val;
              return (
                <button key={val} role="radio" aria-checked={on} onClick={() => pick(r, val)}
                  style={on ? PICK_ON : PICK_OFF}>{lbl}</button>
              );
            })}
          </div>
          {!r.matched && (r.kind || 'w2') === 'w2' && (
            <div style={{ fontSize: 11, color: 'var(--warning)', marginTop: 5 }}>
              {t('cf.notRecognised', 'Not recognised as a draw — until you classify it, this counts as a business expense.')}
            </div>
          )}
          {r.suggested && (r.kind || 'w2') === 'w2' && (
            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 5 }}>
              {t('cf.suggestRetained', 'This amount never repeats — it looks like a distribution, not payroll.')}
            </div>
          )}
        </div>
      ))}
      {/* Escape hatch for a one-off wire the keywords never learned — the case
          that hid a $100k distribution. Deliberately behind a tap. */}
      {!candidates && (
        <button onClick={() => setCandidates(true)} className="pill"
          style={{ marginTop: 14, border: '1px solid var(--rule)', background: 'transparent',
            color: 'var(--ink-2)', cursor: 'pointer' }}>
          {t('cf.findMissing', 'A draw is missing from this list…')}
        </button>
      )}
      {candidates && (
        <div style={{ marginTop: 12, fontSize: 11.5, color: 'var(--ink-3)', lineHeight: 1.45 }}>
          {t('cf.candidateNote', 'Also showing large payments Kanjo does not recognise as draws. Most will be suppliers or bills — leave those on W2 / payroll and they stay ordinary expenses.')}
        </div>
      )}
    </div>
  );
}

// ── RetainedBalanceCard: the retained-earnings pot ─────────────────────────
// Profit the business earned and KEPT, all-time. Two withdrawals are not the
// same event and this card exists to keep them apart:
//   · a W2 draw runs through payroll against the month's own earnings, so it
//     has already reduced retained income and shows in the cash flow as usual;
//   · a retained draw is a pure cash distribution that flushes the pot — not
//     new income, not an expense, so it moves the BALANCE and nothing else.
// The three windows all look at the SAME all-time pot, which is why each shows
// an opening balance: last year's closing figure is the balance as it stood on
// 31 Dec, not last year's profit in isolation.
function RetainedBalanceCard({ view, fmt, dark, onClassify, onSaved }) {
  useT();
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  if (!view || !view.byYear) return null;
  const { prior, current, totalCents } = view.byYear;

  async function saveYearEnd() {
    const v = Number(String(draft).replace(/[^0-9.-]/g, ''));
    if (!Number.isFinite(v)) { setEditing(false); return; }
    setBusy(true);
    try {
      await window.kanjo.cashflow.ownerDraw.setYearEndBalance(prior.year, Math.round(v * 100));
      setEditing(false);
      onSaved?.();
    } catch (e) { console.warn('[yeb] save failed', e); } finally { setBusy(false); }
  }

  const Row = ({ label, cents, tone, strong, sub, action }) => (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
      gap: 12, padding: '9px 16px', borderTop: '1px solid var(--rule)' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: strong ? 14 : 13.5, fontWeight: strong ? 600 : 400,
          color: strong ? 'var(--ink)' : 'var(--ink-2)' }}>{label}</div>
        {sub && <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2 }}>{sub}</div>}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        {cents == null ? (
          <span style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>{t('cf.notKnown', 'not set')}</span>
        ) : (
          <span className="mono" style={{ fontSize: strong ? 17 : 14, fontWeight: strong ? 700 : 500,
            color: tone === 'pos' ? 'var(--positive)' : tone === 'neg' ? 'var(--danger)' : 'var(--ink)' }}>
            {cents < 0 ? `−${fmt(Math.abs(cents))}` : fmt(cents)}
          </span>
        )}
        {action}
      </div>
    </div>
  );

  const editBtn = (
    <button onClick={() => { setDraft(prior.closingCents != null ? (prior.closingCents / 100).toFixed(2) : ''); setEditing(true); }}
      style={{ background: 'transparent', border: 'none', padding: '0 0 0 4px', cursor: 'pointer',
        fontSize: 11.5, color: 'var(--brand)', fontWeight: 600 }}>
      {t('common.edit', 'Edit')}
    </button>
  );

  return (
    <div style={{ padding: '0 20px 14px' }}>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px 8px' }}>
          <div className="label">{t('cf.retainedBalance', 'Retained balance')}</div>
          <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 3 }}>
            {t('cf.retainedSub2', 'Profit kept in the business, and what you have taken from it')}
          </div>
        </div>

        {/* LAST YEAR. Its closing balance stands on its own line: it was
            already income in the year it was earned, it is drawn against
            separately, and the owner often knows it by hand when the bank feed
            does not reach back far enough — hence editable. */}
        <div style={{ padding: '4px 16px 2px', background: 'var(--surface-2)' }}>
          <div className="label" style={{ fontSize: 9.5 }}>
            {t('cf.priorYear', '{y}').replace('{y}', String(prior.year))}
          </div>
        </div>
        {editing ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 16px', borderTop: '1px solid var(--rule)' }}>
            <span style={{ fontSize: 13.5, color: 'var(--ink-2)', flex: 1 }}>{t('cf.yearEndBalance', 'Year-end balance')}</span>
            <input inputMode="decimal" autoFocus value={draft} placeholder="0.00"
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') saveYearEnd(); }}
              style={{ width: 110, padding: '5px 8px', borderRadius: 8, border: '1px solid var(--rule)',
                background: 'var(--surface)', fontFamily: 'var(--mono)', fontSize: 14, textAlign: 'right',
                color: 'var(--ink)', colorScheme: dark ? 'dark' : 'light' }}/>
            <button onClick={saveYearEnd} disabled={busy} className="btn primary"
              style={{ padding: '6px 10px', fontSize: 12.5, cursor: 'pointer' }}>
              {busy ? '…' : t('common.save', 'Save')}
            </button>
          </div>
        ) : (
          <Row label={t('cf.yearEndBalance', 'Year-end balance')} cents={prior.closingCents}
            sub={prior.known ? null : t('cf.yebUnknown', 'Kanjo could not derive it — enter it here')}
            action={editBtn}/>
        )}
        <Row label={t('cf.drawnAgainstPrior', 'Drawn this year')} cents={prior.drawnCents ? -prior.drawnCents : 0}
          tone={prior.drawnCents ? 'neg' : undefined}/>
        <Row label={t('cf.remainingFrom', 'Remaining from {y}').replace('{y}', String(prior.year))}
          cents={prior.remainingCents} strong
          tone={prior.remainingCents != null && prior.remainingCents < 0 ? 'neg' : undefined}/>

        {/* THIS YEAR. No opening balance: on 1 January retained income is zero
            by definition, so the row would say nothing every year. */}
        <div style={{ padding: '10px 16px 2px', background: 'var(--surface-2)', borderTop: '1px solid var(--rule)' }}>
          <div className="label" style={{ fontSize: 9.5 }}>
            {t('cf.thisYear', '{y}').replace('{y}', String(current.year))}
          </div>
        </div>
        <Row label={t('cf.retainedIncomeYtd', 'Retained income')} cents={current.retainedIncomeCents}
          tone={current.retainedIncomeCents < 0 ? 'neg' : 'pos'}/>
        <Row label={t('cf.retainedDraws', 'Retained draws')} cents={current.drawnCents ? -current.drawnCents : 0}
          tone={current.drawnCents ? 'neg' : undefined}/>
        <Row label={t('cf.balanceThisYear', 'Balance')} cents={current.balanceCents} strong
          tone={current.balanceCents < 0 ? 'neg' : undefined}/>

        <div style={{ borderTop: '2px solid var(--rule)' }}>
          <Row label={t('cf.availableTotal', 'Total retained')} cents={totalCents} strong
            tone={totalCents < 0 ? 'neg' : undefined}/>
        </div>

        <div style={{ padding: '10px 16px 14px', borderTop: '1px solid var(--rule)' }}>
          <button onClick={onClassify} className="pill"
            style={{ border: '1px solid var(--rule)', background: 'transparent',
              color: 'var(--ink-2)', cursor: 'pointer' }}>
            {t('cf.classifyDraws', 'Classify owner draws…')}
          </button>
          <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 8, lineHeight: 1.45 }}>
            {t('cf.retainedHelp', 'A W2 draw is paid through payroll and already reduces retained income. Mark a cash distribution as a retained draw and it comes off this balance instead — it is not new income.')}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── ForecastBars: zero-axis bar chart with positive (green) / negative (red) ─
// Bars are each week's NET (inflows − outflows), not the running balance.
// A cumulative balance is monotonic for any profitable business, so plotting it
// through this zero-axis chart drew a straight ramp and hid the thing the
// forecast exists to show: the week payroll and rent land together and the
// business is briefly cash-negative. The balance is still the important number
// — it moves to the tap-through detail below, and to the negative-week
// annotation, which is about running OUT of money and so must stay on balance.
function ForecastBars({ weeks, currency, locale, w = 320, h = 100, onPick }) {
  if (!weeks || !weeks.length) return null;
  const max = Math.max(1, ...weeks.map((w) => Math.abs(w.netCents)));
  const padX = 4, padTop = 8, padBot = 18;
  const innerH = h - padTop - padBot;
  const zeroY = padTop + innerH / 2;
  const gap = 2;
  const bw = ((w - padX * 2) / weeks.length) - gap;
  // First negative-week index, for the inline annotation.
  const firstNegIdx = weeks.findIndex((wk) => wk.closingCents < 0);
  const negWeeks = weeks.filter((wk) => wk.netCents < 0).length;
  const summary = `${weeks.length}-week forecast; ${negWeeks} week${negWeeks === 1 ? '' : 's'} cash-negative`;
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none"
         role="img" aria-label={summary}>
      <title>{summary}</title>
      <line x1={padX} y1={zeroY} x2={w - padX} y2={zeroY} stroke="var(--rule)" strokeWidth="0.5"/>
      {/* "Today" tick: dashed vertical rule between week 0 and week 1. */}
      {weeks.length > 1 && (
        <line x1={padX + bw + gap / 2} y1={padTop} x2={padX + bw + gap / 2} y2={h - padBot}
              stroke="var(--ink-3)" strokeWidth="0.8" strokeDasharray="2 3" opacity="0.7"/>
      )}
      {weeks.map((wk, i) => {
        const v = wk.netCents;
        const len = (Math.abs(v) / max) * (innerH / 2);
        const x = padX + i * (bw + gap);
        const y = v >= 0 ? zeroY - len : zeroY;
        const color = v >= 0 ? 'var(--positive)' : 'var(--danger)';
        return (
          <rect key={wk.weekStart} x={x} y={y} width={bw} height={Math.max(2, len)}
                fill={color} rx="1.5"
                onClick={() => onPick && onPick(wk)}
                style={{ cursor: onPick ? 'pointer' : 'default' }}>
            <title>{`${wk.weekStart}: net ${(v / 100).toFixed(2)} ${currency} · balance ${(wk.closingCents / 100).toFixed(2)}`}</title>
          </rect>
        );
      })}
      {/* Annotation for first negative week. */}
      {firstNegIdx >= 0 && (() => {
        const wk = weeks[firstNegIdx];
        const x = padX + firstNegIdx * (bw + gap) + bw / 2;
        const txt = `${currency === 'USD' ? '$' : ''}${(wk.closingCents / 100).toFixed(0)}`;
        return (
          <g>
            <text x={x} y={h - padBot + 14} textAnchor="middle"
                  fontSize="9.5" fill="var(--danger)" fontFamily="var(--mono)">{txt}</text>
          </g>
        );
      })()}
    </svg>
  );
}

// 12-month grouped bars per month:
//   • left bar = operating net, stacked retained (bottom) + retirement + owner's draw (top)
//   • right bar = owner contributions (capital in, opposite direction)
const CONTRIB_COLOR = '#7A5AE0';
const RETIRE_COLOR = 'var(--mustard,#D39327)';
function OwnerDrawBars({ series, currency, locale, h = 120 }) {
  const max = Math.max(1, ...series.map((m) => Math.max(m.operatingNetCents, m.ownerDrawCents, m.ownerContributionCents, 0)));
  const fmt = (c) => fmtMoneyC(c, currency, locale);
  return (
    <div>
      <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: h }}>
        {series.map((m) => {
          const retained = Math.max(0, m.retainedCents);
          const draw = Math.max(0, m.ownerDrawCents);
          const retire = Math.max(0, m.ownerRetirementCents || 0);
          const contrib = Math.max(0, m.ownerContributionCents);
          return (
            <div key={m.ym} title={`${m.label}: draw ${fmt(m.ownerDrawCents)} · retirement ${fmt(m.ownerRetirementCents || 0)} · retained ${fmt(m.retainedCents)} · contributed ${fmt(m.ownerContributionCents)}${(m.equityDrawCents || 0) > 0 ? ` · ${fmt(m.equityDrawCents)} from prior-year retained earnings (not in pocket)` : ''}`}
                 style={{ flex: 1, display: 'flex', gap: 2, justifyContent: 'center', alignItems: 'flex-end', height: '100%' }}>
              {/* operating stack (= operating net): draw (top) + retirement + retained (bottom) */}
              <div style={{ width: '46%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%' }}>
                <div style={{ width: '100%', height: `${(draw / max) * 100}%`, background: 'var(--accent,#5b8def)', minHeight: draw > 0 ? 2 : 0, borderRadius: '3px 3px 0 0' }}/>
                <div style={{ width: '100%', height: `${(retire / max) * 100}%`, background: RETIRE_COLOR, minHeight: retire > 0 ? 2 : 0 }}/>
                <div style={{ width: '100%', height: `${(retained / max) * 100}%`, background: 'var(--positive,#2e9e5b)', minHeight: retained > 0 ? 2 : 0 }}/>
              </div>
              {/* contributions */}
              <div style={{ width: '46%', display: 'flex', alignItems: 'flex-end', height: '100%' }}>
                <div style={{ width: '100%', height: `${(contrib / max) * 100}%`, background: CONTRIB_COLOR, minHeight: contrib > 0 ? 2 : 0, borderRadius: '3px 3px 0 0' }}/>
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
        {series.map((m, i) => (
          <div key={m.ym} style={{ flex: 1, textAlign: 'center', fontSize: 9, color: 'var(--ink-3)', whiteSpace: 'nowrap', overflow: 'hidden' }}>
            {i % 2 === 0 ? m.label : ''}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 14, marginTop: 10, fontSize: 12, color: 'var(--ink-2)', flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: 'var(--accent,#5b8def)' }}/>Owner's draw</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: 'var(--positive,#2e9e5b)' }}/>Retained in business</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: RETIRE_COLOR }}/>Retirement</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: CONTRIB_COLOR }}/>Contributions</span>
      </div>
    </div>
  );
}

// ── EarningsTable: pocket / draw / retained / 401k by month ───────────────
// A one-off lump beyond the year's own earnings is a distribution of prior
// retained earnings: it shows as a sub-line and is excluded from Total. The
// balance it draws against lives in the Retained balance tile.
function EarningsTable({ series, currency, locale }) {
  // Display in $K by default (owner: "$30,316.42 → $30.3"); the switch flips
  // to whole dollars. Cents are never shown in this table.
  const [kMode, setKMode] = React.useState(true);
  const rows = [...series].reverse(); // latest month first
  if (!rows.some((m) => (m.pocketCents || 0) !== 0)) return null;
  const fmt = (cents) => {
    const c = window.kanjo?.demo?.scale ? window.kanjo.demo.scale(cents) : cents;
    return new Intl.NumberFormat(locale || 'en-US', {
      style: 'currency', currency: currency || 'USD',
      ...(kMode ? { notation: 'compact', maximumFractionDigits: 1 } : { maximumFractionDigits: 0 }),
    }).format((c || 0) / 100);
  };
  const cell = (cents, { bold = false, signed = false } = {}) => (
    <div className="mono" style={{
      textAlign: 'right', fontSize: 12.5, fontWeight: bold ? 600 : 400,
      color: !cents ? 'var(--ink-4)' : (signed && cents < 0 ? 'var(--danger)' : 'var(--ink)'),
    }}>
      {cents ? fmt(cents) : '—'}
    </div>
  );
  const grid = { display: 'grid', gridTemplateColumns: '1.1fr 1fr 1fr 1fr 1fr', gap: 8, alignItems: 'baseline' };
  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--rule)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div className="label">Earnings by month</div>
        <div role="tablist" aria-label="Number format" style={{ display: 'flex', gap: 3, background: 'var(--surface-3,#f0f0f3)', borderRadius: 6, padding: 2 }}>
          {[['k', '$K'], ['full', '$']].map(([k, lbl]) => {
            const on = (k === 'k') === kMode;
            return (
              <button key={k} role="tab" aria-selected={on} onClick={() => setKMode(k === 'k')}
                style={{ padding: '2px 8px', borderRadius: 4, border: 'none', cursor: 'pointer', fontSize: 10.5, fontWeight: 600,
                  background: on ? 'var(--surface,#fff)' : 'transparent', color: on ? 'var(--ink)' : 'var(--ink-3)' }}>{lbl}</button>
            );
          })}
        </div>
      </div>
      <div style={{ ...grid, marginBottom: 6 }}>
        <div className="label">Month</div>
        <div className="label" style={{ textAlign: 'right' }}>Total</div>
        <div className="label" style={{ textAlign: 'right' }}>Draw</div>
        <div className="label" style={{ textAlign: 'right' }}>Retained</div>
        <div className="label" style={{ textAlign: 'right' }}>401k</div>
      </div>
      {rows.map((m) => (
        <React.Fragment key={m.ym}>
          <div style={{ ...grid, padding: '5px 0', borderTop: '1px solid var(--rule)' }}>
            <div style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>{m.label} <span style={{ color: 'var(--ink-4)', fontSize: 10.5 }}>{m.ym.slice(0, 4)}</span></div>
            {cell(m.pocketCents || 0, { bold: true, signed: true })}
            {cell(m.ownerDrawCents)}
            {cell(m.retainedCents || 0, { signed: true })}
            {cell(m.ownerRetirementCents || 0)}
          </div>
          {(m.equityDrawCents || 0) > 0 && (
            <div style={{ fontSize: 11, color: 'var(--ink-3)', padding: '0 0 5px', textAlign: 'right' }}>
              + {fmt(m.equityDrawCents)} drawn from prior-year retained earnings · not in Total
            </div>
          )}
          {/* The year-end balance used to be rendered here, between December and
              January. It now lives in the Retained balance tile, where it is
              the prior-year line the owner draws against — one home for one
              number, and editable there. */}
        </React.Fragment>
      ))}
    </div>
  );
}

// The year-end-balance divider row and the reserve-reconcile modal used to
// live here. Both are gone: the year-end balance is now the prior-year line
// of the Retained balance tile (editable there), and reconciling which draws
// were prior-year money is done by classifying each draw as W2 / Retained /
// Retained-last-year, which replaced the amount-repetition guess this modal
// existed to confirm.

function useCashflowData(reloadNonce = 0) {
  const [state, setState] = React.useState({ loading: true });
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!window.kanjo) return;
      try {
        const claims = await window.kanjo.auth.currentClaims().catch(() => null);
        if (!claims) { setState({ loading: false }); return; }
        const profile = await window.kanjo.db.userProfile.get(claims.sub);
        // Recognize the latest bank deposits as sales BEFORE deriving the owner's
        // pocket, so the "In your pocket" chart settles revenue to the month it
        // was earned (same basis as the Today screen). Idempotent; best-effort.
        await window.kanjo.revenue?.sales?.syncFromDeposits?.().catch(() => {});
        const [position, forecast, ownerDraw, retained] = await Promise.all([
          window.kanjo.cashflow.position.getPosition(),
          window.kanjo.cashflow.forecast.build(),
          window.kanjo.cashflow.ownerDraw.monthlySummary({ months: 12 }).catch(() => null),
          // The pot reaches back further than the 12-month table above it: it is
          // all-time, so profit kept in earlier years still counts.
          window.kanjo.cashflow.retainedBalance.retainedView().catch(() => null),
        ]);
        // Minimum funding needs: 12-week all-in expense rate graded against the
        // bank balance (danger < 1 week of expenses, minimum funding = 3 weeks).
        const funding = await window.kanjo.cashflow.position.fundingNeeds({ cashCents: position.cashCents }).catch(() => null);
        // Tier + admin drive the CSV upload section's visibility. Free →
        // no upload. Basic → upload (no Plaid in plan). Pro → Plaid only.
        // PAYG → BOTH (operator picks). Admin → BOTH (testing path).
        const tier = String(claims['custom:tier'] || 'free').toLowerCase();
        let groups = claims['cognito:groups'];
        if (typeof groups === 'string') {
          groups = groups.replace(/^\[|\]$/g, '').split(/[\s,]+/).filter(Boolean);
        }
        const isAdmin = Array.isArray(groups) && groups.includes('admins');
        if (!cancelled) setState({
          loading: false, position, forecast, ownerDraw, retained, funding,
          currency: profile?.currency || position.currency || 'USD',
          locale: profile?.locale || 'en-US',
          userId: claims.sub,
          tier,
          isAdmin,
          // SMB advice puts the trigger at 30%; honour an explicit profile
          // override if the operator has set one.
          creditUtilTarget: Number.isFinite(profile?.creditUtilizationTarget)
            ? profile.creditUtilizationTarget
            : 0.30,
        });
      } catch (e) { console.warn('[Cashflow] load failed', e); if (!cancelled) setState({ loading: false }); }
    })();
    return () => { cancelled = true; };
  }, [reloadNonce]);
  return state;
}

// Product decision 2026-07-06: the CSV transaction-upload tile is HIDDEN from
// the UI to steer operators to Plaid (the upload UX is poor). The importer and
// BankUploadSection code are kept intact — flip this flag to re-enable the tile
// (tier logic below still applies). Admin still has the CSV import on the Admin
// screen (a separate, testing-only surface).
const CSV_UPLOAD_UI_ENABLED = false;

// Tier + admin → whether to offer CSV import. Basic and PAYG don't (or
// optionally don't) use Plaid; admin always sees both surfaces for testing.
function shouldOfferCsvUpload({ tier, isAdmin }) {
  if (!CSV_UPLOAD_UI_ENABLED) return false;
  if (isAdmin) return true;
  return tier === 'basic' || tier === 'payg';
}

// ── BankUploadSection ─────────────────────────────────────────────────────
// CSV importer surfaced inline on the Cash flow screen. Validation +
// parsing live in app/src/lib/cashflow/bankUpload.js; this component only
// owns the file-picker UX and the result/error display.
function BankUploadSection({ userId, onImported, isAdmin, tier }) {
  const inputRef = React.useRef(null);
  const [phase, setPhase] = React.useState('idle'); // idle | reading | done | error
  const [result, setResult] = React.useState(null);
  const [err, setErr] = React.useState('');

  const handleFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = ''; // allow re-uploading the same file
    if (!f) return;
    setPhase('reading'); setErr(''); setResult(null);
    try {
      const importer = window.kanjo?.cashflow?.bankUpload?.importCsv;
      if (!importer) throw new Error('Upload module not loaded.');
      const r = await importer(f, userId);
      setResult(r);
      setPhase('done');
      onImported?.();
    } catch (e2) {
      setErr(String(e2?.message || e2));
      setPhase('error');
    }
  };

  return (
    <div style={{ padding: '0 20px 14px' }}>
      <div className="card" style={{ padding: 18 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <div className="label">Import bank transactions</div>
          {isAdmin && tier !== 'basic' && tier !== 'payg' && (
            <span style={{ fontSize: 10, color: 'var(--ink-3,#999)', textTransform: 'uppercase', letterSpacing: 0.5 }}>admin</span>
          )}
        </div>
        <p style={{ fontSize: 13, color: 'var(--ink-2,#666)', margin: '8px 0 14px', lineHeight: 1.5 }}>
          Export your transactions as CSV from your bank (Chase, Bank of America, Wells Fargo, AmEx and others all support this), then drop the file here. We'll match columns automatically.
        </p>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={handleFile}
          style={{ display: 'none' }}
        />
        <button
          type="button"
          onClick={() => inputRef.current && inputRef.current.click()}
          disabled={phase === 'reading'}
          className="btn primary"
          style={{ width: '100%' }}
        >
          {phase === 'reading' ? 'Reading CSV…' : 'Choose CSV file'}
        </button>
        {phase === 'done' && result && (
          <div role="status" style={{ marginTop: 12, fontSize: 13, color: 'var(--positive,#2e9e5b)', lineHeight: 1.5 }}>
            ✓ Imported {result.savedCount} transaction{result.savedCount === 1 ? '' : 's'} from <strong>{result.fileName}</strong>
            {result.skippedCount > 0 && <span style={{ color: 'var(--ink-3,#999)' }}> · {result.skippedCount} already on file</span>}.
          </div>
        )}
        {phase === 'error' && (
          <ErrorBanner style={{ marginTop: 12 }}>{err}</ErrorBanner>
        )}
        <div style={{ fontSize: 11, color: 'var(--ink-3,#999)', marginTop: 10, lineHeight: 1.5 }}>
          Stays on this device. Re-uploading the same file is safe — duplicates are skipped automatically. Limit 2 MB per file.
        </div>
      </div>
    </div>
  );
}

function relTime(ts) {
  if (!ts) return '';
  const mins = Math.max(0, Math.round((Date.now() - ts) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return new Date(ts).toLocaleDateString();
}

// ── Cash-flow (Sankey) diagram ──────────────────────────────────────────────
// Sales channels (left) → revenue → cost categories (right). Sources and the
// cost side always balance: a profit month adds a "Net profit" node on the
// right; a loss month adds an "Other funds" node on the left, so both columns
// sum to the same total and the ribbons fill the height.
//
// Navigation: defaults to the LAST completed calendar month; ‹ › step months.
// The current (in-progress) month can't be a full month, so at that position we
// show a rolling LAST 30 DAYS instead — and › is capped there (no future).
const CF_COLORS = {
  instore: '#7C3AED', delivery: '#2563EB', shortfall: '#94A3B8',
  cogs: '#D97706', wages: '#0D9488', rent_utilities: '#EA8C1B',
  taxes: '#DC2626', supplies: '#7C5CD6', insurance: '#059669', net: '#16A34A',
  maintenance: '#DB2777', other: '#64748B',
};
function cfLabel(id) {
  switch (id) {
    case 'instore':        return t('cf.instore', 'In-store');
    case 'delivery':       return t('cf.delivery', 'Delivery');
    case 'shortfall':      return t('cf.shortfall', 'Other funds');
    case 'cogs':           return t('cf.cogs', 'COGS');
    case 'wages':          return t('cf.wages', 'Wages');
    case 'rent_utilities': return t('cf.rentUtil', 'Rent & util.');
    case 'maintenance':    return t('cf.maintenance', 'Maintenance');
    case 'taxes':          return t('cf.taxes', 'Taxes');
    case 'supplies':       return t('cf.supplies', 'Supplies');
    case 'insurance':      return t('cf.insurance', 'Insurance');
    case 'other':          return t('cf.other', 'Other costs');
    case 'net':            return t('cf.net', 'Net profit');
    default:               return id;
  }
}
function cfMonthLabel(m) {
  const [y, mm] = String(m).split('-').map(Number);
  try { return new Intl.DateTimeFormat(navigator.language || 'en-US', { month: 'long', year: 'numeric' }).format(new Date(y, mm - 1, 1)); }
  catch { return m; }
}
// Compact money for the Sankey labels, which sit on ONE line beside the name:
// $18.5k reads at a glance where $18,466 crowds the label off the card. Under a
// thousand there is nothing to compact — $484 is already short, and "$0.5k"
// would be both longer and vaguer.
function cfMoneyK(c) {
  const v = (c || 0) / 100;
  const a = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (a < 1000) return `${sign}$${Math.round(a)}`;
  const trim = (n) => (n >= 100 ? Math.round(n).toString() : n.toFixed(1).replace(/\.0$/, ''));
  if (a >= 1_000_000) return `${sign}$${trim(a / 1_000_000)}M`;
  return `${sign}$${trim(a / 1000)}k`;
}

// ALWAYS thousands, unlike cfMoneyK above which leaves anything under $1,000
// alone. A figure that silently switches units between months can't be compared
// at a glance, and comparing months is the whole job of the YTD line — so $940
// reads "$0.9k", not "$940". One decimal until the number is big enough that it
// is noise (≥100k → whole).
function cfMoneyAlwaysK(cents, currency, locale) {
  const c = window.kanjo?.demo?.scale ? window.kanjo.demo.scale(cents) : cents;
  const k = (c || 0) / 100_000;
  const digits = Math.abs(k) >= 100 ? 0 : 1;
  try {
    return new Intl.NumberFormat(locale || 'en-US', {
      style: 'currency', currency: currency || 'USD',
      minimumFractionDigits: digits, maximumFractionDigits: digits,
    }).format(k) + 'k';
  } catch { return `$${k.toFixed(digits)}k`; }
}

function cfMoney(c) {
  try { return new Intl.NumberFormat(navigator.language || 'en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format((c || 0) / 100); }
  catch { return '$' + Math.round((c || 0) / 100); }
}

function CashFlowSankey({ dark = false }) {
  useT();
  const pnlLib = window.kanjo?.revenue?.pnl;
  // Default to the last COMPLETED month; the current month renders as last-30d.
  const [month, setMonth] = React.useState(() => (pnlLib ? pnlLib.shiftMonth(pnlLib.currentMonth(), -1) : ''));
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const isCurrent = !!pnlLib && month === pnlLib.currentMonth(); // → last 30 days
  React.useEffect(() => {
    if (!pnlLib || !month) { setLoading(false); return; }
    let live = true; setLoading(true);
    (async () => {
      try { const d = isCurrent ? await pnlLib.rolling30Pnl() : await pnlLib.monthlyPnl(month); if (live) setData(d); }
      catch { if (live) setData(null); }
      finally { if (live) setLoading(false); }
    })();
    return () => { live = false; };
  }, [month, isCurrent]);

  const hasData = !!(data && (data.revenueCents > 0 || (data.costs && data.costs.length)));
  const periodLabel = isCurrent ? t('cf.last30', 'Last 30 days') : cfMonthLabel(month);
  const navBtn = (disabled) => ({
    width: 30, height: 30, borderRadius: 8, border: '1px solid var(--rule)',
    background: 'var(--surface-2)', color: 'var(--ink-2)', fontSize: 16,
    cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.4 : 1, lineHeight: 1,
  });

  let svg = null;
  if (hasData) {
    // Geometry lives in lib/revenue/sankeyLayout.js so the "everything fits in
    // the frame" invariant is testable — the spine used to be sized from the
    // frame height, which includes label overhang, and hung below the diagram.
    const L = window.kanjo.revenue.sankeyLayout;
    const { H, denom, sources: S, targets: Tg, spine } = L.sankeyLayout(data);
    const { VBW, TOP, BARW, LBAR_L, LBAR_R, CBAR_L, CBAR_R, RBAR_L, RBAR_R } = L;
    const ribbon = (xa, ya, ha, xb, yb, hb) => {
      const xc = (xa + xb) / 2;
      return `M${xa},${ya} C${xc},${ya} ${xc},${yb} ${xb},${yb} L${xb},${yb + hb} C${xc},${yb + hb} ${xc},${ya + ha} ${xa},${ya + ha} Z`;
    };
    svg = (
      <svg viewBox={`0 0 ${VBW} ${H}`} width="100%" style={{ display: 'block' }} role="img" aria-label={t('cf.title', 'Cash flow')}>
        {S.map((s) => <path key={'ls' + s.id} d={ribbon(LBAR_R, s.y, s.h, CBAR_L, s.y, s.h)} fill={CF_COLORS[s.id] || '#888'} opacity="0.26" />)}
        {Tg.map((tg) => <path key={'rt' + tg.id} d={ribbon(CBAR_R, tg.y, tg.h, RBAR_L, tg.y, tg.h)} fill={CF_COLORS[tg.id] || '#888'} opacity="0.26" />)}
        <rect x={CBAR_L} y={spine.y} width={BARW} height={spine.h} rx="2" fill="var(--ink-3)" opacity="0.5" />
        {S.map((s) => (
          <g key={'l' + s.id}>
            <rect x={LBAR_L} y={s.y} width={BARW} height={s.h} rx="2" fill={CF_COLORS[s.id] || '#888'} />
            {/* Name and amount on ONE line — two lines per label forced the
                declutter pass to shove labels far from their own slice. */}
            <text x={LBAR_L - 7} y={s.ly + 3} textAnchor="end" style={{ fontSize: 11 }}>
              <tspan style={{ fontWeight: 600, fill: 'var(--ink)' }}>{cfLabel(s.id)}</tspan>
              <tspan style={{ fontSize: 9.5, fill: 'var(--ink-3)' }}>{` ${cfMoneyK(s.cents)} · ${Math.round(s.cents / denom * 100)}%`}</tspan>
            </text>
          </g>
        ))}
        {Tg.map((tg) => (
          <g key={'r' + tg.id}>
            <rect x={RBAR_L} y={tg.y} width={BARW} height={tg.h} rx="2" fill={CF_COLORS[tg.id] || '#888'} />
            <text x={RBAR_R + 7} y={tg.ly + 3} style={{ fontSize: 11 }}>
              <tspan style={{ fontWeight: 600, fill: 'var(--ink)' }}>{cfLabel(tg.id)}</tspan>
              <tspan style={{ fontSize: 9.5, fill: 'var(--ink-3)' }}>{` ${cfMoneyK(tg.cents)} · ${Math.round(tg.cents / denom * 100)}%`}</tspan>
            </text>
          </g>
        ))}
      </svg>
    );
  }

  return (
    <div className="card" style={{ margin: '0 20px 14px', padding: '14px 12px 12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 6px 10px' }}>
        <div>
          <div className="label" style={{ margin: 0 }}>{t('cf.title', 'Cash flow')}</div>
          <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 1 }}>
            {periodLabel}{hasData ? ` · ${cfMoney(data.revenueCents)} ${t('cf.revenue', 'revenue')}` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button aria-label={t('cf.prev', 'Previous month')} onClick={() => pnlLib && setMonth((m) => pnlLib.shiftMonth(m, -1))} style={navBtn(false)}>‹</button>
          <button aria-label={t('cf.next', 'Next month')} disabled={isCurrent}
                  onClick={() => { if (pnlLib && !isCurrent) setMonth((m) => { const nxt = pnlLib.shiftMonth(m, 1); return nxt > pnlLib.currentMonth() ? m : nxt; }); }}
                  style={navBtn(isCurrent)}>›</button>
        </div>
      </div>
      {loading ? (
        <div style={{ height: 180 }} />
      ) : hasData ? svg : (
        <div style={{ padding: '20px 12px', textAlign: 'center', color: 'var(--ink-3)', fontSize: 13, lineHeight: 1.5 }}>
          {t('cf.empty', 'Close a sales day and connect a card to see where your money flows.')}
        </div>
      )}
    </div>
  );
}

function CashflowDashboard({ dark = false, persona = 'owner' }) {
  // reloadNonce bumps after a CSV import so position/forecast re-derive
  // with the new transactions included.
  const [reloadNonce, setReloadNonce] = React.useState(0);
  const s = useCashflowData(reloadNonce);
  // This screen otherwise formats money through <KMoney>; the retained-balance
  // card needs a plain string formatter, so use the same helper the rest of the
  // file does. Defaults guard the first render, before the profile has loaded.
  const fmt = (c) => fmtMoneyC(c, s.currency || 'USD', s.locale || 'en-US');
  const [pickedWeek, setPickedWeek] = React.useState(null);
  const [cfgOpen, setCfgOpen] = React.useState(false);
  const [classifyOpen, setClassifyOpen] = React.useState(false);
  const offerCsv = !s.loading && s.userId && shouldOfferCsvUpload(s);
  if (s.loading) return (
    <KShell dark={dark} tab="reports" persona={persona}>
      <KHeader back big title="Cash flow" />
      <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <SkeletonCard height={200}/>
        <SkeletonCard height={140}/>
        <SkeletonCard height={120}/>
      </div>
    </KShell>
  );

  const p = s.position;
  const weeks = s.forecast.weeks;
  const m = moneyPartsC(p.cashCents, s.currency, s.locale);
  const week0 = weeks[0];
  const negativeWeeks = weeks.filter((w) => w.closingCents < 0).length;

  return (
    <KShell dark={dark} tab="reports" persona={persona}>
      <div style={{ height: '100%', overflowY: 'auto', paddingBottom: 16 }}>
        <KHeader back big title="Cash flow" subtitle={p.asOf ? `Synced ${relTime(p.asOf)}` : '—'}
          right={<GearButton label={t('settings.ownerDraw', "Owner's draw settings")} onClick={() => setCfgOpen(true)} />} />
        <Sheet open={cfgOpen} onClose={() => setCfgOpen(false)} title={t('account.ownerDraw', "Owner's draw")}>
          <OwnerDrawSettings />
        </Sheet>

        {p.stale && (
          <div style={{ margin: '0 20px 14px', padding: '11px 14px',
                        background: 'var(--surface-3, #f0f0f3)', color: 'var(--ink-2,#666)',
                        borderRadius: 12, fontSize: 12.5, display: 'flex', gap: 9, alignItems: 'flex-start', lineHeight: 1.45 }}>
            <svg width="15" height="15" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0, marginTop: 2 }}>
              <circle cx="9" cy="9" r="7.5"/><path d="M9 5v4.5M9 12.5h.01"/>
            </svg>
            <div>
              {p.asOf > 0
                ? `Heads up — your figures are based on data last updated ${relTime(p.asOf)}. Connect a bank to sync newer transactions and refresh.`
                : 'No bank data yet on this device. Connect a bank to see your live cash position. (Data stays on this device and is cleared when you sign out.)'}
            </div>
          </div>
        )}

        {/* In your pocket leads (owner request 2026-07-08) — the bank-balance
            card with the funding gauge follows it below. */}
        {/* In your pocket = owner's draw + retained (12 months) */}
        {s.ownerDraw && s.ownerDraw.series && s.ownerDraw.series.length > 0 && (() => {
          const last = s.ownerDraw.series[s.ownerDraw.series.length - 1];
          const currentDraw = s.ownerDraw.currentDrawCents || 0;
          const currentRetirement = s.ownerDraw.currentRetirementCents || 0;
          const currentRetained = last.retainedCents || 0;
          // The owner's full take = cash drawn + 401(k) set aside + profit retained.
          const currentPocket = currentDraw + currentRetirement + currentRetained;
          const totalRetained = s.ownerDraw.series.reduce((a, b) => a + (b.retainedCents || 0), 0);
          const totalRetirement = s.ownerDraw.totalRetirementCents || 0;
          const totalPocket = (s.ownerDraw.totalDrawCents || 0) + totalRetirement + totalRetained;
          // Year to date, beside the month's hero figure — the month answers
          // "how did I just do", this answers "how is the year going".
          const ytd = window.kanjo.cashflow.ownerDraw.ytdPocket(s.ownerDraw.series);
          return (
            <div style={{ padding: '0 20px 14px' }}>
              {/* This card LEADS the report, so the pocket figure gets the hero
                  treatment (same `figure xl` scale as the cash position below). */}
              <div className="card" style={{ padding: 22 }}>
                <div className="label">In your pocket · {last.label}</div>
                <div style={{ marginTop: 10, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
                  <KMoney cents={currentPocket} currency={s.currency} locale={s.locale} size="xl"
                          tone={currentPocket >= 0 ? 'positive' : 'danger'}/>
                  {ytd.year && (
                    <div className="mono" style={{ fontSize: 12.5, whiteSpace: 'nowrap', textAlign: 'right',
                      color: ytd.cents >= 0 ? 'var(--positive)' : 'var(--danger)' }}>
                      {cfMoneyAlwaysK(ytd.cents, s.currency, s.locale)}
                      <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}> YTD</span>
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginTop: 8 }}>
                  <span style={{ color: 'var(--accent,#5b8def)', fontWeight: 700 }}>Drawn</span>{' '}
                  <KMoney cents={currentDraw} currency={s.currency} locale={s.locale} style={{ fontSize: 12.5 }}/>
                  {' · '}
                  <span style={{ color: 'var(--positive,#2e9e5b)', fontWeight: 700 }}>Retained</span>{' '}
                  <KMoney cents={currentRetained} currency={s.currency} locale={s.locale}
                          tone={currentRetained >= 0 ? 'positive' : 'danger'} style={{ fontSize: 12.5 }}/>
                  {currentRetirement > 0 ? <> {' · '}<span style={{ color: 'var(--positive,#2e9e5b)', fontWeight: 700 }}>Retirement</span>{' '}<KMoney cents={currentRetirement} currency={s.currency} locale={s.locale} style={{ fontSize: 12.5 }}/></> : null}
                  {s.ownerDraw.currentContributionCents > 0 ? <> {' · '}<KMoney cents={s.ownerDraw.currentContributionCents} currency={s.currency} locale={s.locale} style={{ fontSize: 12.5 }}/> contributed</> : null}
                </div>
                {(s.ownerDraw.currentEquityDrawCents || 0) > 0 && (
                  <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 4 }}>
                    + <KMoney cents={s.ownerDraw.currentEquityDrawCents} currency={s.currency} locale={s.locale} style={{ fontSize: 11.5 }}/> drawn from prior-year retained earnings — already counted the year it was earned, so not in this month's pocket.
                  </div>
                )}
                <div style={{ marginTop: 14 }}>
                  <OwnerDrawBars series={s.ownerDraw.series} currency={s.currency} locale={s.locale}/>
                </div>
                <EarningsTable series={s.ownerDraw.series} currency={s.currency} locale={s.locale}/>
                <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 10 }}>
                  12 mo: <KMoney cents={s.ownerDraw.totalDrawCents} currency={s.currency} locale={s.locale} style={{ fontSize: 11.5 }}/> drawn ·{' '}
                  <KMoney cents={totalRetained} currency={s.currency} locale={s.locale}
                          tone={totalRetained >= 0 ? 'positive' : 'danger'} style={{ fontSize: 11.5 }}/> retained ·{' '}
                  {totalRetirement > 0 ? <><KMoney cents={totalRetirement} currency={s.currency} locale={s.locale} style={{ fontSize: 11.5 }}/> to retirement ·{' '}</> : null}
                  <KMoney cents={totalPocket} currency={s.currency} locale={s.locale}
                          tone={totalPocket >= 0 ? 'positive' : 'danger'} style={{ fontSize: 11.5 }}/> in your pocket
                  {s.ownerDraw.totalContributionCents > 0 ? <> {' · '}<KMoney cents={s.ownerDraw.totalContributionCents} currency={s.currency} locale={s.locale} style={{ fontSize: 11.5 }}/> contributed</> : null}
                  {(s.ownerDraw.totalEquityDrawCents || 0) > 0 ? <> {' · '}<KMoney cents={s.ownerDraw.totalEquityDrawCents} currency={s.currency} locale={s.locale} style={{ fontSize: 11.5 }}/> from prior-year earnings</> : null}
                </div>
                {s.ownerDraw.keywords.length === 0 && (
                  <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 10 }}>
                    No owner-draw keywords set — tap the gear above to add them so draws aren't counted as expenses.
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Bank balance — cash position + minimum-funding gauge */}
        <div style={{ padding: '0 20px 14px' }}>
          <div className="card" style={{ padding: 22 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
              <div className="label">Cash position</div>
              {!p.stale && p.asOf > 0 && (
                <span className="pill positive">
                  <span className="dot" style={{ background: 'currentColor' }}/>
                  LIVE
                </span>
              )}
            </div>
            <div className="figure xl" style={{ marginTop: 10 }}>
              {renderMoney(m)}
            </div>
            <FundingGauge funding={s.funding} cashCents={p.cashCents} currency={s.currency} locale={s.locale}/>
            {p.breakdown.length > 0 && (
              <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--rule)',
                            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
                {p.breakdown.map((b) => (
                  <div key={b.plaidAccountId} style={{ minWidth: 0 }}>
                    <div className="label">{b.name}</div>
                    <div style={{ marginTop: 4 }}>
                      <KMoney cents={b.balanceCents} currency={s.currency} locale={s.locale}
                              tone={b.balanceCents < 0 ? 'danger' : undefined}
                              style={{ fontSize: 14, fontWeight: 500 }}/>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {p.creditUtilization.totalLimitCents > 0 && (
              <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--rule)',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            fontSize: 13 }}>
                <div style={{ color: 'var(--ink-3)' }}>Credit utilization</div>
                <div className="mono" style={{
                  color: (p.creditUtilization.utilizationPct || 0) > (s.creditUtilTarget || 0.30) ? 'var(--warning)' : 'var(--ink-2)',
                  fontWeight: 500,
                }}>
                  <KPct value={(p.creditUtilization.utilizationPct || 0) * 100} style={{ fontSize: 13 }}/> of <KMoney cents={p.creditUtilization.totalLimitCents} currency={s.currency} locale={s.locale} style={{ fontSize: 13 }}/>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Retained balance — the pot, above the forecast. */}
        <RetainedBalanceCard view={s.retained} fmt={fmt} dark={dark}
          onClassify={() => setClassifyOpen(true)}
          onSaved={() => setReloadNonce((n) => n + 1)} />
        <Sheet open={classifyOpen} onClose={() => setClassifyOpen(false)}
          title={t('cf.classifyTitle', 'Owner draws')}>
          <ClassifyDrawsSheet fmt={fmt} onChanged={() => setReloadNonce((n) => n + 1)} />
        </Sheet>

        {/* 13-week forecast */}
        <div style={{ padding: '0 20px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10, paddingLeft: 4 }}>
            <div>
              <div className="label">{t('cf.forecast13', '13-week forecast')}</div>
              <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2 }}>
                {t('cf.forecastSub', 'Each week: cash in minus cash out')}
              </div>
            </div>
            {negativeWeeks > 0 && <span className="pill danger" style={{ fontSize: 11 }}>{negativeWeeks} negative week{negativeWeeks === 1 ? '' : 's'}</span>}
          </div>
          <div className="card" style={{ padding: 16 }}>
            <ForecastBars weeks={weeks} currency={s.currency} locale={s.locale} onPick={setPickedWeek}/>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6,
                          fontSize: 11, color: 'var(--ink-3)' }}>
              <span>{weeks[0]?.weekStart}</span>
              <span>{weeks[weeks.length - 1]?.weekEnd}</span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 8, lineHeight: 1.45 }}>
              {s.forecast.seasonal
                ? 'Projected sales follow last year’s monthly pattern (each week scaled by how this time of year ran a year ago).'
                : 'Projected sales use your 4-week average. Once a full year of data is on file, the projection follows last year’s monthly pattern instead of a flat line.'}
            </div>
            {week0 && (
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--rule)',
                            display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, fontSize: 12 }}>
                <div>
                  <div className="label">Opening</div>
                  <div style={{ marginTop: 2 }}>
                    <KMoney cents={week0.openingCents} currency={s.currency} locale={s.locale}
                            tone={week0.openingCents < 0 ? 'danger' : undefined}
                            style={{ fontSize: 14, fontWeight: 500 }}/>
                  </div>
                </div>
                <div>
                  <div className="label">Net week</div>
                  <div style={{ marginTop: 2 }}>
                    {week0.netCents >= 0 ? '+' : ''}
                    <KMoney cents={week0.netCents} currency={s.currency} locale={s.locale}
                            tone={week0.netCents >= 0 ? 'positive' : 'danger'}
                            style={{ fontSize: 14, fontWeight: 500 }}/>
                  </div>
                </div>
                <div>
                  <div className="label">Closing</div>
                  <div style={{ marginTop: 2 }}>
                    <KMoney cents={week0.closingCents} currency={s.currency} locale={s.locale}
                            tone={week0.closingCents < 0 ? 'danger' : undefined}
                            style={{ fontSize: 14, fontWeight: 500 }}/>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Cash-flow diagram — the last report tile: where the money flowed
            (last completed month by default; the current month shows last 30 days). */}
        <CashFlowSankey dark={dark} />

        {/* Import bank transactions — kept at the BOTTOM so the cash position,
            pocket, and forecast lead; the "Synced …" date stays in the header. */}
        {offerCsv && (
          <BankUploadSection
            userId={s.userId}
            tier={s.tier}
            isAdmin={s.isAdmin}
            onImported={() => setReloadNonce((n) => n + 1)}
          />
        )}

      </div>

      <Sheet open={!!pickedWeek} onClose={() => setPickedWeek(null)}
             title={pickedWeek ? `Week of ${pickedWeek.weekStart}` : ''}>
        {pickedWeek && (
          <div>
            <KPIRow>
              <KPITile label="Opening" value={fmtMoneyC(pickedWeek.openingCents, s.currency, s.locale)}/>
              <KPITile label="Net" value={fmtMoneyC(pickedWeek.netCents, s.currency, s.locale)}
                       tone={pickedWeek.netCents >= 0 ? 'positive' : 'danger'}/>
              <KPITile label="Closing" value={fmtMoneyC(pickedWeek.closingCents, s.currency, s.locale)}
                       tone={pickedWeek.closingCents < 0 ? 'danger' : 'neutral'}/>
            </KPIRow>
            {pickedWeek.inflows != null && (
              <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <KPITile label="In" value={fmtMoneyC(pickedWeek.inflows, s.currency, s.locale)} tone="positive"/>
                <KPITile label="Out" value={fmtMoneyC(pickedWeek.outflows, s.currency, s.locale)} tone="warning"/>
              </div>
            )}
            <div style={{ marginTop: 18, fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.5 }}>
              {pickedWeek.closingCents < 0
                ? 'Closing balance projected below zero. Consider moving a payable or scheduling a transfer.'
                : 'Projection based on recurring inflows and outflows.'}
            </div>
          </div>
        )}
      </Sheet>
    </KShell>
  );
}

Object.assign(window, { CashflowDashboard, ForecastBars });
